/**
 * IntakeEventUploadPage — `/intake-events/new` (Chunk 9.T.3).
 *
 * Permet à un broker authentifié de déposer un ou plusieurs PDFs reçus
 * (typiquement un PDF de soumission reçu d'un client externe). Le backend
 * crée un `intake_event` orphan (`intake_link_id=NULL`,
 * `source_kind='file_upload'`, `status='pending_classification'`) auquel
 * sont rattachés les fichiers (table `app.files` + polymorphe
 * `app.document_links`).
 *
 * Après upload réussi, on navigue vers `/intake-events/:id` (page détail —
 * `IntakeEventDetailPage`) où le broker résout manuellement via le
 * `ResolveEventDialog` 9.G.2 réutilisé.
 *
 * Strict OUT scope (PO 9.T.3) :
 *   - Pas de classifier AI ici (Wave 9.T.4)
 *   - Pas de materialize, ni de création company/project auto
 *   - Pas d'extraction Q&A automatique (Wave 9.T.5)
 *
 * Brand strict + doctrine [docs/design/frontend-ux-doctrine.md] :
 *   - Inter, slate-50 page bg / white surface, focus-visible ring brand-500
 *   - FileDropzone hero (le composant générique factor du PublicFileDropzone)
 *   - Submit CTA disabled tant que 0 fichier sélectionné
 *   - Erreurs inline bilingual (ApiError du wire ADR 0027 P-3) via
 *     `getApiErrorMessage(err, lang)`
 *   - Anti-leak : pas de console.log du fichier/event/storage_path
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { FileDropzone } from "../../components/FileDropzone";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useUploadBrokerIntake } from "../../lib/queries/intakeEvents";

export function IntakeEventUploadPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();
  const upload = useUploadBrokerIntake();

  const [files, setFiles] = useState<readonly File[]>([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const filesMissing = files.length === 0;
  const isSubmitting = upload.isPending;

  const errorMessage = useMemo(() => {
    if (!upload.error) return null;
    return getApiErrorMessage(upload.error, lang);
  }, [upload.error, lang]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (filesMissing || isSubmitting) return;

    const fd = new FormData();
    for (const f of files) fd.append("files", f, f.name);

    upload.mutate(fd, {
      onSuccess: (data) => {
        // 9.T.4 — `?auto_classify=1` déclenche l'auto-classify one-shot
        // côté `IntakeEventDetailPage`. Le search param est cleared après
        // trigger (replace: true), donc un F5 broker ne re-déclenche pas.
        navigate(`/intake-events/${data.intake_event_id}?auto_classify=1`, {
          replace: false,
        });
      },
    });
  };

  return (
    <div className="mx-auto w-full max-w-3xl">
      <header className="space-y-1">
        <nav aria-label={t("common.breadcrumb")} className="mb-1.5 text-sm">
          <Link
            to="/intake-events"
            className="focus-visible:ring-brand-500 rounded text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("reception_flow.receipts")}
          </Link>
          <span className="mx-2 text-slate-300" aria-hidden="true">
            ›
          </span>
          <span className="text-slate-900">{t("intake_events.upload.breadcrumb.current")}</span>
        </nav>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {t("intake_events.upload.title")}
        </h1>
        <p className="text-sm text-slate-600">{t("intake_events.upload.subtitle")}</p>
      </header>

      <form
        onSubmit={onSubmit}
        noValidate
        className="mt-6 space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] sm:p-7"
      >
        <FileDropzone
          files={files}
          onFilesChange={setFiles}
          disabled={isSubmitting}
          errorMessage={
            submitAttempted && filesMissing
              ? t("intake_events.upload.files.errors.required")
              : undefined
          }
          i18nNamespace="intake_events.upload.files"
        />

        {errorMessage && (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-red-200 bg-red-50/60 px-3.5 py-2.5 text-sm text-red-800"
          >
            {errorMessage}
          </div>
        )}

        <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-500">{t("intake_events.upload.legal_hint")}</p>
          <div className="flex items-center gap-2">
            <Link
              to="/intake-events"
              className="focus-visible:ring-brand-500 inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("intake_events.upload.actions.cancel")}
            </Link>
            <button
              type="submit"
              disabled={filesMissing || isSubmitting}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting
                ? t("intake_events.upload.actions.submitting")
                : t("intake_events.upload.actions.submit")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
